import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@thuthi/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@thuthi/ui/components/dialog";
import { Input } from "@thuthi/ui/components/input";
import { randomAlphanumericString, topicUrl, validTopic } from "../app/utils";
import subscriptionManager from "../app/SubscriptionManager";
import poller from "../app/Poller";
import DialogFooter from "./DialogFooter";
import session from "../app/Session";
import routes from "./routes";
import accountApi from "../app/AccountApi";
import { UnauthorizedError } from "../app/errors";
import type { Subscription } from "../app/types";

/**
 * 구독 = 서버 구독 목록(PUT /api/v2/me/subscriptions/{topic}) + 로컬 구독. 서버가 읽기 권한도 확인한다(403).
 * /api/v2 는 이 서버만 다루므로 예전 "다른 서버 쓰기"·다른 서버 로그인·토픽 예약은 없다.
 */
export const subscribeTopic = async (baseUrl: string, topic: string, opts: Partial<Subscription>): Promise<Subscription> => {
  await accountApi.addSubscription(baseUrl, topic);
  return subscriptionManager.add(baseUrl, topic, opts);
};

interface SubscribeDialogProps {
  open: boolean;
  subscriptions?: Subscription[];
  onCancel: () => void;
  onSuccess: (subscription: Subscription) => void;
}

const SubscribeDialog = (props: SubscribeDialogProps) => {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // 구독 목록은 Dexie 라이브 쿼리라 첫 렌더에선 아직 undefined 다(다이얼로그는 닫혀 있어도 렌더된다)
  const existingTopicUrls = (props.subscriptions ?? []).map((s) => topicUrl(s.baseUrl, s.topic));
  const subscribeButtonEnabled = !busy && validTopic(topic) && !existingTopicUrls.includes(topicUrl(config.base_url, topic));

  const handleSubscribe = async () => {
    setBusy(true);
    setError("");
    try {
      console.log(`[SubscribeDialog] Subscribing to topic ${topic}`);
      const subscription = await subscribeTopic(config.base_url, topic, {});
      poller.pollInBackground(subscription); // Dangle!
      setTopic("");
      props.onSuccess(subscription);
    } catch (e) {
      console.log(`[SubscribeDialog] Subscribing to topic ${topic} failed`, e);
      if (e instanceof UnauthorizedError) {
        await session.resetAndRedirect(routes.login);
        return;
      }
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={(open) => !open && props.onCancel()}>
      {/* MUI fullScreen(useMediaQuery breakpoints.down("sm")) 대응: <sm 에서는 CSS 로 풀스크린 처리 */}
      <DialogContent className="flex flex-col gap-4 overflow-y-auto max-sm:left-0 max-sm:top-0 max-sm:h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0">
        <DialogHeader>
          <DialogTitle>{t("subscribe_dialog_subscribe_title")}</DialogTitle>
          <DialogDescription>{t("subscribe_dialog_subscribe_description")}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 grow space-y-2">
          <div role="row" className="flex flex-col items-stretch gap-1 pb-2 sm:flex-row sm:items-start sm:gap-2">
            <Input
              autoFocus
              id="topic"
              placeholder={t("subscribe_dialog_subscribe_topic_placeholder")}
              value={topic}
              onChange={(ev) => setTopic(ev.target.value)}
              onKeyDown={(ev) => ev.key === "Enter" && subscribeButtonEnabled && handleSubscribe()}
              type="text"
              maxLength={64}
              aria-label={t("subscribe_dialog_subscribe_topic_placeholder")}
              className="flex-1"
            />
            <Button variant="ghost" className="shrink-0" onClick={() => setTopic(randomAlphanumericString(16))}>
              {t("subscribe_dialog_subscribe_button_generate_topic_name")}
            </Button>
          </div>
        </div>
        <DialogFooter status={error}>
          <Button variant="ghost" onClick={props.onCancel}>
            {t("subscribe_dialog_subscribe_button_cancel")}
          </Button>
          <Button onClick={handleSubscribe} disabled={!subscribeButtonEnabled}>
            {t("subscribe_dialog_subscribe_button_subscribe")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubscribeDialog;
